import type { APICommonResponse } from 'src/types/common.types';
import { checkLogin } from 'src/utils/auth';
import utils from 'src/utils/utils';

import { axiosInstance } from '@/store/server/common/client';

import type {
  BuyStickerRequest,
  BuyStickerResponse,
  DrawLuckyDrawResponse,
  FinishBlockMiniGameRequest,
  FinishBlockMiniGameResponse,
  FinishMiniGameRequest,
  FinishMiniGameResponse,
  GetBlockMiniGameInitRequest,
  GetBlockMiniGameInitResponse,
  GetBlockMiniGameRankingRequest,
  GetBlockMiniGameRankingResponse,
  GetLuckyDrawHistoryResponse,
  GetLuckyDrawTicketCountResponse,
  GetMatchEventHistoryResponse,
  GetMiniGameRankingRequest,
  GetMiniGameRankingResponse,
  GetMiniGameStatusRequest,
  GetMiniGameStatusResponse,
  GetPointSaveRequest,
  GetPointSaveResponse,
  GetPointUseDetailRequest,
  GetPointUseDetailResponse,
  GetPointUseRequest,
  GetPointUseResponse,
  GetRealTimeCardRequest,
  GetRealTimeCardResponse,
  GetRealTimeEditionRequest,
  GetRealTimeEditionResponse,
  GetRepresentCardRequest,
  GetRepresentCardResponse,
  GetRepresentGalleryRequest,
  GetRepresentGalleryResponse,
  GetStickerListRequest,
  GetStickerListResponse,
  GetTopCollectorPeriodRequest,
  GetTopCollectorPeriodResponse,
  GetTopCollectorRequest,
  GetTopCollectorResponse,
  GetTopItemsRequest,
  GetTopItemsResponse,
  GetTopNotificationRequest,
  GetTopNotificationResponse,
  GetVerifyMintingRequest,
  GetVerifyMintingResponse,
  PostVerifyMintingRequest,
  PostVerifyMintingResponse,
  PredictMatchEventRequest,
  PredictMatchEventResponse,
  ReceiveLuckyDrawPrizeRequest,
  ReceiveLuckyDrawPrizeResponse,
  RegisterLuckyDrawCodeRequest,
  RegisterLuckyDrawCodeResponse,
  RegisterOfflineEventRequest,
  SavePointRequest,
  SavePointResponse,
  SetRepresentCardRequest,
  SetRepresentCardResponse,
  StartMiniGameResponse,
  UsePointRequest,
  UsePointResponse,
} from './football.dto';

export const footballService = {
  // [메인] 실시간 카드 등록 현황_에디션 조회
  getRealTimeEdition: async ({
    pack_idx,
  }: GetRealTimeEditionRequest): Promise<APICommonResponse<GetRealTimeEditionResponse>> => {
    return axiosInstance.market.public.get(`/football/real-time/edition/${pack_idx}`);
  },

  // [블럭 미니게임] 랭킹 조회
  getBlockMiniGameRanking: async ({
    originals_idx,
  }: GetBlockMiniGameRankingRequest): Promise<APICommonResponse<GetBlockMiniGameRankingResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(
      `/football/minigame/block/ranking/${originals_idx}`,
    );
  },

  // [블럭 미니게임] 조회 (초기화)
  getBlockMiniGameInit: async ({
    originals_idx,
  }: GetBlockMiniGameInitRequest): Promise<APICommonResponse<GetBlockMiniGameInitResponse>> => {
    return axiosInstance.market.private.get(`/football/minigame/block/init/${originals_idx}`);
  },

  // [블럭 미니게임] 종료
  finishBlockMiniGame: async ({
    originals_idx,
    amount,
  }: FinishBlockMiniGameRequest): Promise<APICommonResponse<FinishBlockMiniGameResponse>> => {
    return axiosInstance.market.private.post(`/football/minigame/block/${originals_idx}`, { amount });
  },

  // [탑 콜렉터] 조회
  getTopCollector: async ({
    pack_idx,
    season,
  }: GetTopCollectorRequest): Promise<APICommonResponse<GetTopCollectorResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(
      `/football/top-collector/${pack_idx}`,
      {
        params: { season },
      },
    );
  },

  // [탑 콜렉터] 기간 조회
  getTopCollectorPeriod: async ({
    pack_idx,
    season,
  }: GetTopCollectorPeriodRequest): Promise<APICommonResponse<GetTopCollectorPeriodResponse>> => {
    return axiosInstance.market.public.get(`/football/top-collector/${pack_idx}/season`, {
      params: { season },
    });
  },

  // [포인트교환소] 포인트 모으기 조회
  getPointSave: async (params: GetPointSaveRequest): Promise<APICommonResponse<GetPointSaveResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get('/point/save', {
      params,
    });
  },

  // [포인트교환소] 포인트 모으기 포인트받기
  savePoint: async ({ method_code, method_idx }: SavePointRequest): Promise<APICommonResponse<SavePointResponse>> => {
    return axiosInstance.market.private.post('/point/save', { method_code, method_idx });
  },

  // [메인] 상단 알림 팝업 조회
  getTopNotification: async ({
    originals_idx,
  }: GetTopNotificationRequest): Promise<APICommonResponse<GetTopNotificationResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(
      `/football/popup-alarm/${originals_idx}`,
    );
  },

  // [메인] 대표카드 조회
  getRepresentCard: async ({
    originals_idx,
  }: GetRepresentCardRequest): Promise<APICommonResponse<GetRepresentCardResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(
      `/football/represent/${originals_idx}`,
    );
  },

  // [메인] 대표 갤러리 조회
  getRepresentGallery: async ({
    originals_idx,
  }: GetRepresentGalleryRequest): Promise<APICommonResponse<GetRepresentGalleryResponse>> => {
    return axiosInstance.market.public.get(`/football/represent/gallery/${originals_idx}`);
  },

  // [메인] 실시간 카드 등록 현황
  getRealTimeCard: async ({
    pack_idx,
    edition_title,
  }: GetRealTimeCardRequest): Promise<APICommonResponse<GetRealTimeCardResponse>> => {
    return axiosInstance.market.public.get(`/football/real-time/${pack_idx}`, {
      params: { edition_title },
    });
  },

  // [포인트라운지] 오프라인 이벤트 참여
  registerOfflineEvent: async ({ method_idx, method_code }: RegisterOfflineEventRequest) => {
    return axiosInstance.market.private.post('/point/offline', { method_code, method_idx });
  },

  // [포인트교환소] 포인트 사용하기 조회
  getPointUse: async (params: GetPointUseRequest): Promise<APICommonResponse<GetPointUseResponse>> => {
    return axiosInstance.market.public.get('/point/use', { params });
  },

  // [포인트라운지] 포인트 사용하기 상세조회
  getPointUseDetail: async ({
    method_idx,
  }: GetPointUseDetailRequest): Promise<APICommonResponse<GetPointUseDetailResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(`/point/use/${method_idx}`);
  },

  // [포인트라운지] 포인트 사용하기 - 응모하기
  usePoint: async ({
    method_code,
    method_idx,
    amount,
    name,
    phone,
    birth,
    post_no,
    address,
    address_detail,
    id_card_file,
  }: UsePointRequest): Promise<APICommonResponse<UsePointResponse>> => {
    const formData = utils.jsonToFormData({
      method_code,
      method_idx,
      amount,
      name,
      phone,
      birth,
      post_no,
      address,
      address_detail,
      id_card_file,
    });

    return axiosInstance.market.private.post('/point/use', formData, {
      headers: {
        contextType: 'multipart/form-data',
      },
    });
  },

  // [미니게임] 미니게임 상태 조회
  getMiniGameStatus: async ({
    originals_idx,
  }: GetMiniGameStatusRequest): Promise<APICommonResponse<GetMiniGameStatusResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(
      `/football/minigame/${originals_idx}`,
    );
  },

  // [미니게임] 미니게임 시작
  startMiniGame: async (originals_idx: number): Promise<APICommonResponse<StartMiniGameResponse>> => {
    return axiosInstance.market.private.post(`/football/minigame/start/${originals_idx}`);
  },

  // [미니게임] 미니게임 종료
  finishMiniGame: async ({
    originals_idx,
    amount,
  }: FinishMiniGameRequest): Promise<APICommonResponse<FinishMiniGameResponse>> => {
    return axiosInstance.market.private.post(`/football/minigame/finish/${originals_idx}`, { amount });
  },

  // [미니게임] 미니게임 랭킹 조회
  getMiniGameRanking: async ({
    originals_idx,
  }: GetMiniGameRankingRequest): Promise<APICommonResponse<GetMiniGameRankingResponse>> => {
    return (checkLogin() ? axiosInstance.market.private : axiosInstance.market.public).get(
      `/football/minigame/ranking/${originals_idx}`,
    );
  },

  // [메인] 대표카드 설정
  setRepresentCard: async ({
    originals_idx,
    metadata_idx,
    width,
    height,
    stickers,
  }: SetRepresentCardRequest): Promise<APICommonResponse<SetRepresentCardResponse>> => {
    return axiosInstance.market.private.post(`/football/represent/${originals_idx}`, {
      metadata_idx,
      width,
      height,
      stickers,
    });
  },

  // [메인] 인기 상품 TOP 5
  getTopItems: async ({ originals_idx }: GetTopItemsRequest): Promise<APICommonResponse<GetTopItemsResponse>> => {
    return axiosInstance.market.public.get(`/football/product-top/${originals_idx}`);
  },

  /**
   * ---------- 스티커 관련 API ----------
   */

  // [스티커] 스티커 목록 조회
  getStickerList: async ({
    originals_idx,
  }: GetStickerListRequest): Promise<APICommonResponse<GetStickerListResponse>> => {
    return axiosInstance.market.private.get(`/football/sticker/${originals_idx}`);
  },

  // [스티커] 스티커 구매
  buySticker: async ({
    originals_idx,
    sticker_idx,
  }: BuyStickerRequest): Promise<APICommonResponse<BuyStickerResponse>> => {
    return axiosInstance.market.private.post(`/football/sticker/${originals_idx}`, { sticker_idx });
  },

  /**
   * ---------- 팝업 관련 API ----------
   */

  // [럭키드로우] 코드 등록
  registerLuckyDrawCode: async (
    data: RegisterLuckyDrawCodeRequest,
  ): Promise<APICommonResponse<RegisterLuckyDrawCodeResponse>> => {
    return axiosInstance.market.private.post('/football/barcelona/code', data);
  },

  // [럭키드로우] 보유 응모권 조회
  getLuckyDrawTicketCount: async (): Promise<APICommonResponse<GetLuckyDrawTicketCountResponse>> => {
    return axiosInstance.market.private.get('/football/barcelona/apply');
  },

  // [럭키드로우] 당첨 내역 조회
  getLuckyDrawHistory: async (): Promise<APICommonResponse<GetLuckyDrawHistoryResponse>> => {
    return axiosInstance.market.private.get('/football/barcelona/apply/hist');
  },

  // [럭키드로우] 수령하기
  receiveLuckyDrawPrize: async (
    data: ReceiveLuckyDrawPrizeRequest,
  ): Promise<APICommonResponse<ReceiveLuckyDrawPrizeResponse>> => {
    return axiosInstance.market.private.post('/football/barcelona/receive', data);
  },

  // [럭키드로우] 카드 뽑기
  drawLuckyDraw: async (): Promise<APICommonResponse<DrawLuckyDrawResponse>> => {
    return axiosInstance.market.private.post('/football/barcelona/card');
  },

  // [예측 이벤트] 제출 여부 조회
  getMatchEventHistory: async (): Promise<APICommonResponse<GetMatchEventHistoryResponse>> => {
    return axiosInstance.market.private.get('/football/barcelona/guess');
  },

  // [예측 이벤트] 예측하기
  predictMatchEvent: async (data: PredictMatchEventRequest): Promise<APICommonResponse<PredictMatchEventResponse>> => {
    return axiosInstance.market.private.post('/football/barcelona/guess', data);
  },

  // [내컬렉션] 엠블럼카드 수령 여부 조회
  getVerifyMinting: async ({
    pack_idx,
    card_name,
  }: GetVerifyMintingRequest): Promise<APICommonResponse<GetVerifyMintingResponse>> => {
    return axiosInstance.market.private.get(`/card/verify-minting/${pack_idx}`, { params: { card_name } });
  },

  // [내컬렉션] 엠블럼카드 받기
  postVerifyMinting: async ({
    pack_idx,
    metadata_idx,
  }: PostVerifyMintingRequest): Promise<APICommonResponse<PostVerifyMintingResponse>> => {
    return axiosInstance.market.private.post(`/card/verify-minting/${pack_idx}`, { metadata_idx });
  },
};
